import type { UntypedFunction } from '@mnd-frontend/ts';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCEPT_DOC_FILES,
  FILE_ERROR_MESSAGE_TIMEOUT,
  MAX_FILE_SIZE_IN_BYTES,
  MAX_FILENAME_LENGTH,
  type UploadingStates,
} from '../constants';
import ErrorMessage from '../ErrorMessage';
import FileInputField from '../FileInputField';
import UploadWrapper from '../UploadWrapper';
import { createFileValidators, type ValidatorProps } from '../validators/file';
import DocumentUploadPreview from './DocumentUploadPreview';

type Props = {
  fileName?: string;
  caption?: string;
  loading?: boolean;
  isDraft?: boolean;
  disabled?: boolean;
  action: UntypedFunction;
  deleteCallback?: UntypedFunction;
  validationErrorMsg?: UntypedFunction;
  validatorProps?: ValidatorProps;
};

const DocumentUploader = ({
  fileName,
  caption,
  disabled = false,
  loading,
  isDraft,
  action,
  deleteCallback,
  validationErrorMsg,
  validatorProps = {},
}: Props) => {
  const {
    validFileFormats = ACCEPT_DOC_FILES,
    maxFilenameLength = MAX_FILENAME_LENGTH,
    maxFileSize = MAX_FILE_SIZE_IN_BYTES,
  } = validatorProps;
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadingStates>('IDLE');
  const [error, setError] = useState('');
  const [t] = useTranslation();

  const fileValidators = createFileValidators({ validFileFormats, maxFilenameLength, maxFileSize });

  const handleValidationError = (error: any) => {
    if (error.code === 'too_long') {
      return setError(t('components_file-upload_error_too-long-name'));
    } else if (error.code === 'error.fileformat') {
      return setError(t('components_file-upload_error_format'));
    } else if (error.code === 'document_too_large') {
      return maxFileSize === MAX_FILE_SIZE_IN_BYTES
        ? setError(t('document_too_large'))
        : setError(t('contacts_file_error_too_large'));
    }
    if (validationErrorMsg) {
      const msg = validationErrorMsg(error);
      setError(msg);
    }
  };

  const handleFileDelete = () => {
    if (deleteCallback) deleteCallback();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setError('');
    setStatus('LOADING');
    try {
      if (!!fileValidators && fileValidators.length > 0) {
        const promises = fileValidators.map(validator => validator(file));
        await Promise.all(promises);
      }
      await action(file);
      setStatus('IDLE');
    } catch (e) {
      handleValidationError(e);
      setStatus('IDLE');
    }
  };

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      if (timeout) {
        setError('');
      }
    }, FILE_ERROR_MESSAGE_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  return (
    <UploadWrapper>
      {error ? (
        <ErrorMessage caption={error} />
      ) : (
        <>
          {fileName && status !== 'LOADING' && (
            <DocumentUploadPreview
              fileName={fileName}
              disabled={disabled}
              isDraft={isDraft}
              error={error}
              onChange={() => inputRef.current?.click()}
              handleFileDelete={handleFileDelete}
            />
          )}
          <FileInputField
            inputRef={inputRef}
            status={status}
            loading={loading}
            caption={caption || t('components_file-upload_select-file')}
            acceptFormats={validFileFormats}
            onChange={handleFileSelect}
            disabled={disabled}
          />
        </>
      )}
    </UploadWrapper>
  );
};

export default DocumentUploader;
